import {useEffect, useMemo, useState, memo, useCallback} from 'react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Badge} from '@common-web/components/ui/badge';
import {Button} from '@common-web/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@common-web/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@common-web/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  PaginationEllipsis,
} from '@common-web/components/ui/pagination';
import {Input} from '@common-web/components/ui/input';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  FileText,
  AlertCircle,
  Download,
  X,
  LayoutGrid,
  List,
  Search,
} from 'lucide-react';
import {RowIssuesCardView} from '@client/pages/payroll/components/RowIssuesCardView';
import {
  ValidationReport,
  ValidationError,
  PayrollRowExportType,
} from '@api/lib/payrollProcessing/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import clsx from 'clsx';
import {CollapseChevron} from '@common-web/components/collapse-chevron';
import {FileIdBadge} from '@client/pages/payroll/components/FileIdBadge';
import {exportFileValidationErrorReportCsv} from '@client/pages/payroll/utils/exportUtils';
import {SkipInvalidAlert} from '@client/pages/payroll/components/SkipInvalidAlert';
import {toTitleCase} from '@common/lib/utils';
import {StructuralErrorCard} from '@client/pages/payroll/components/StructuralErrorCard';

export const ValidationResults = memo(
  ({
    report,
    canSkipInvalidRows,
    onSkipInvalidRows,
    onConfigureUnmapped,
    onExportTransformedCsv,
  }: {
    report: ValidationReport;
    canSkipInvalidRows: boolean;
    onSkipInvalidRows: () => Promise<void>;
    onConfigureUnmapped: () => void;
    onExportTransformedCsv: (rowExportType: PayrollRowExportType) => Promise<void>;
  }) => {
    const [errorDetailsOpen, setErrorDetailsOpen] = useState(true);
    const [showUnmappedColumnsWarning, setShowUnmappedColumnsWarning] = useState(false);
    const [showMissingColumnsWarning, setShowMissingColumnsWarning] = useState(false);
    const [rowErrorTypeFilter, setRowErrorTypeFilter] = useState<string>('ALL');
    const [rowIssuesView, setRowIssuesView] = useState<RowIssuesView>('cards');
    const [employeeIdSearch, setEmployeeIdSearch] = useState('');

    const summary = report?.summary || {};
    const structuralErrors = report?.structuralValidation?.errors || [];
    const errors = report?.rowValidation?.errors || [];

    const filteredErrors = useMemo(() => {
      if (!employeeIdSearch.trim()) return errors;
      const searchTerm = employeeIdSearch.trim().toLowerCase();
      return errors.filter((error) => {
        const employeeId = error.rowContext?.EMPLOYEE_ID?.toLowerCase() || '';
        return employeeId.includes(searchTerm);
      });
    }, [errors, employeeIdSearch]);

    const onExportErrorReportCsv = useCallback(() => {
      exportFileValidationErrorReportCsv(report);
    }, [report]);

    useEffect(() => {
      if (structuralErrors.length > 0) {
        const hasUnmapped = structuralErrors.some((e) => e.type === 'UNMAPPED_COLUMNS_DETECTED');
        const hasMissing = structuralErrors.some((e) => e.type === 'MISSING_COLUMNS_IN_FILE');
        if (hasUnmapped) {
          setShowUnmappedColumnsWarning(true);
        } else if (hasMissing) {
          setShowMissingColumnsWarning(true);
        }
      }
    }, [structuralErrors]);

    return (
      <>
        <div className='flex flex-col gap-6 min-w-0'>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-internal-brand-gray flex items-center justify-between'>
                  Total Rows
                  <FileText className='h-5 w-5 text-internal-brand-blue' />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='text-3xl font-bold text-internal-brand-navy'>
                  {summary.totalRecords ?? 0}
                </div>
                <p className='text-xs text-internal-brand-gray mt-1'>Rows processed</p>
              </CardContent>
            </Card>
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-internal-brand-gray flex items-center justify-between'>
                  Valid Rows
                  <CheckCircle2 className='h-5 w-5 text-emerald' />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='text-3xl font-bold text-emerald'>{summary.validRecords ?? 0}</div>
                <p className='text-xs text-internal-brand-gray mt-1'>No issues found</p>
              </CardContent>
            </Card>
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-internal-brand-gray flex items-center justify-between'>
                  Rows with Warnings
                  <AlertTriangle className='h-5 w-5 text-amber' />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='text-3xl font-bold text-amber'>{summary.warningRecords ?? 0}</div>
                <p className='text-xs text-internal-brand-gray mt-1'>Review recommended</p>
              </CardContent>
            </Card>
            <Card className='border-gray-200 shadow-sm hover:shadow-md transition-shadow'>
              <CardHeader className='pb-3'>
                <CardTitle className='text-sm font-medium text-internal-brand-gray flex items-center justify-between'>
                  Invalid Rows
                  <AlertCircle className='h-5 w-5 text-red-600' />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className='text-3xl font-bold text-red-600'>{summary.invalidRecords ?? 0}</div>
                <p className='text-xs text-internal-brand-gray mt-1'>Must be fixed</p>
              </CardContent>
            </Card>
          </div>
          {structuralErrors.length > 0 && (
            <StructuralErrorsList
              structuralErrors={structuralErrors}
              onConfigureUnmapped={onConfigureUnmapped}
            />
          )}
          <Collapsible open={errorDetailsOpen} onOpenChange={setErrorDetailsOpen}>
            <Card className='border-gray-200 shadow-sm min-w-0 max-w-full overflow-hidden'>
              <CardHeader className='hover:bg-gray-50 transition-colors'>
                <div className='flex items-center justify-between'>
                  <CollapsibleTrigger asChild>
                    <CardTitle className='text-lg font-semibold text-internal-brand-navy flex items-center gap-2 cursor-pointer flex-1'>
                      <AlertCircle className='h-5 w-5 text-internal-brand-blue' />
                      Row Issues
                      {errors.length > 0 && (
                        <Badge className='bg-red-600 hover:bg-red-700 text-white border-0 font-semibold ml-2'>
                          {errors.length}
                        </Badge>
                      )}
                    </CardTitle>
                  </CollapsibleTrigger>
                  <div className='flex items-center gap-4'>
                    {errors.length > 0 && (
                      <>
                        <div className='relative' onClick={(e) => e.stopPropagation()}>
                          <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400' />
                          <Input
                            type='text'
                            placeholder='Search by Employee ID...'
                            value={employeeIdSearch}
                            onChange={(e) => setEmployeeIdSearch(e.target.value)}
                            className='pl-9 pr-8 h-9 w-56 text-sm border-gray-200'
                          />
                          {employeeIdSearch && (
                            <button
                              onClick={() => setEmployeeIdSearch('')}
                              className='absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-sm hover:bg-gray-100'
                            >
                              <X className='h-3.5 w-3.5 text-gray-400' />
                            </button>
                          )}
                        </div>
                        <RowIssuesViewToggle view={rowIssuesView} onViewChange={setRowIssuesView} />
                      </>
                    )}
                    <CollapsibleTrigger asChild>
                      <div className='flex items-center gap-4 cursor-pointer'>
                        <div className='text-right'>
                          <div className='text-2xl font-bold text-internal-brand-navy'>
                            {(
                              ((summary.validRecords || 0) / (summary.totalRecords || 1)) *
                              100
                            ).toFixed(1)}
                            %
                          </div>
                          <div className='text-xs text-internal-brand-gray'>Success Rate</div>
                        </div>
                        <CollapseChevron
                          open={errorDetailsOpen}
                          className={'h-5 w-5 text-internal-brand-gray'}
                        />
                      </div>
                    </CollapsibleTrigger>
                  </div>
                </div>
              </CardHeader>
              <CollapsibleContent>
                <CardContent className='pt-0 min-w-0'>
                  {errors.length === 0 ? (
                    <div className='text-center py-8'>
                      <CheckCircle2 className='h-12 w-12 text-emerald mx-auto mb-3' />
                      <div className='text-lg font-medium text-emerald'>No Row Errors</div>
                      <div className='text-sm text-internal-brand-gray'>
                        Every row passed validation successfully
                      </div>
                    </div>
                  ) : filteredErrors.length === 0 && employeeIdSearch ? (
                    <div className='text-center py-8'>
                      <Search className='h-12 w-12 text-gray-300 mx-auto mb-3' />
                      <div className='text-lg font-medium text-gray-600'>No matching errors</div>
                      <div className='text-sm text-internal-brand-gray'>
                        No errors found for Employee ID containing "{employeeIdSearch}"
                      </div>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => setEmployeeIdSearch('')}
                        className='mt-4'
                      >
                        Clear search
                      </Button>
                    </div>
                  ) : (
                    <div className='space-y-4 min-w-0'>
                      {employeeIdSearch && (
                        <div className='flex items-center gap-2 text-sm text-internal-brand-gray bg-gray-50 px-3 py-2 rounded-lg'>
                          <Search className='h-4 w-4' />
                          <span>
                            Showing {filteredErrors.length} of {errors.length} errors matching
                            Employee ID "{employeeIdSearch}"
                          </span>
                          <button
                            onClick={() => setEmployeeIdSearch('')}
                            className='ml-auto text-internal-brand-blue hover:underline text-xs'
                          >
                            Clear filter
                          </button>
                        </div>
                      )}
                      {filteredErrors.some((e) => e.level === 'HIGH') &&
                        !structuralErrors.some((e) => e.level === 'HIGH') &&
                        !employeeIdSearch && (
                          <Alert className='border-red-200 bg-red-50 mb-4 flex items-center'>
                            <AlertDescription className='text-red-800'>
                              <div
                                className={clsx(
                                  'text-sm font-semibold',
                                  canSkipInvalidRows && 'mb-4',
                                )}
                              >
                                The system cannot process the following members. Please update their
                                information in your system and upload a revised file.
                                {canSkipInvalidRows &&
                                  'If you wish to proceed with processing and skip these members, click "Skip invalid rows and proceed" below.'}
                              </div>
                              {canSkipInvalidRows && (
                                <SkipInvalidAlert
                                  onExportInvalidRows={() => onExportTransformedCsv('INVALID_ROWS')}
                                  onSkipInvalidRows={onSkipInvalidRows}
                                  invalidRowCount={Number(summary.invalidRecords || 0)}
                                />
                              )}
                            </AlertDescription>
                          </Alert>
                        )}
                      {rowIssuesView === 'cards' ? (
                        <RowIssuesCardView
                          errors={filteredErrors}
                          onExportErrorReportCsv={onExportErrorReportCsv}
                        />
                      ) : (
                        <>
                          <IssueTypeSummary
                            errors={filteredErrors}
                            activeFilter={rowErrorTypeFilter}
                            onFilterChange={setRowErrorTypeFilter}
                          />
                          <RowIssuesTable
                            errors={filteredErrors}
                            onExportErrorReportCsv={onExportErrorReportCsv}
                            errorTypeFilter={rowErrorTypeFilter}
                            onErrorTypeFilterChange={setRowErrorTypeFilter}
                          />
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
        <AlertDialog
          open={showUnmappedColumnsWarning}
          onOpenChange={(open) => setShowUnmappedColumnsWarning(open)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Unmapped Columns Detected</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogDescription>
              The following columns from your uploaded files are not mapped to any field:
              <div className={'overflow-y-auto max-h-[300px] pt-4'}>
                {Object.entries(
                  structuralErrors.find((e) => e.type === 'UNMAPPED_COLUMNS_DETECTED')
                    ?.rowContext ?? {},
                ).map(([columnName, fileId], idx) => (
                  <div
                    key={idx}
                    className='flex items-center gap-2 even:bg-gray-50 even:border-y even:border-gray-200 p-2'
                  >
                    <FileIdBadge fileId={fileId} />
                    <div className='font-mono text-internal-brand-navy font-medium text-sm px-2 py-1'>
                      {columnName}
                    </div>
                  </div>
                ))}
              </div>
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onConfigureUnmapped}
                className='bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white'
              >
                Configure Columns
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <AlertDialog
          open={showMissingColumnsWarning}
          onOpenChange={(open) => setShowMissingColumnsWarning(open)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Columns Missing From Uploaded Files</AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogDescription>
              The following columns were expected but not found in your uploaded files:
              <div className={'overflow-y-auto max-h-[300px] pt-4'}>
                {Object.entries(
                  structuralErrors.find((e) => e.type === 'MISSING_COLUMNS_IN_FILE')?.rowContext ??
                    {},
                ).map(([columnName, fileId], idx) => (
                  <div
                    key={idx}
                    className='flex items-center gap-2 even:bg-gray-50 even:border-y even:border-gray-200 p-2'
                  >
                    <FileIdBadge fileId={fileId} />
                    <div className='font-mono text-internal-brand-navy font-medium text-sm px-2 py-1'>
                      {columnName}
                    </div>
                  </div>
                ))}
              </div>
            </AlertDialogDescription>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={onConfigureUnmapped}
                className='bg-internal-brand-blue hover:bg-internal-brand-blue-dark text-white'
              >
                Configure Columns
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  },
);

const IssueTypeSummary = memo(
  ({
    errors,
    activeFilter,
    onFilterChange,
    className,
  }: {
    errors: ValidationError[];
    activeFilter: string;
    onFilterChange: (type: string) => void;
    className?: string;
  }) => {
    const issueGroups = useMemo(() => {
      return Object.entries(
        [...errors].reduce(
          (acc, error) => {
            acc[error.type] = {
              level: error.level === 'HIGH' ? 'HIGH' : acc[error.type]?.level || 'LOW',
              count: (acc[error.type]?.count || 0) + 1,
            };
            return acc;
          },
          {} as Record<string, {level: ValidationError['level']; count: number}>,
        ),
      ).sort((a, b) => {
        if (a[1].level !== b[1].level) {
          return a[1].level === 'HIGH' ? -1 : 1;
        }
        return b[1].count - a[1].count;
      });
    }, [errors]);

    if (issueGroups.length === 0) return null;

    return (
      <div className={clsx('py-2 flex flex-wrap gap-3', className)}>
        {issueGroups.map(([type, {level, count}]) => (
          <button
            key={`${type}-${level}`}
            onClick={() => onFilterChange(activeFilter === type ? 'ALL' : type)}
            className={clsx(
              'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all',
              'border hover:shadow-sm',
              activeFilter === type
                ? level === 'HIGH'
                  ? 'bg-red-100 border-red-300 text-red-800'
                  : 'bg-amber/10 border-amber text-amber'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50',
            )}
          >
            <span
              className={clsx(
                'w-2 h-2 rounded-full shrink-0',
                level === 'HIGH' ? 'bg-red-600' : 'bg-amber',
              )}
            />
            <span>{toTitleCase(type.replace(/_/g, ' '))}</span>
            <Badge
              variant='secondary'
              className={clsx(
                'text-xs px-1.5 py-0',
                activeFilter === type
                  ? level === 'HIGH'
                    ? 'bg-red-200 hover:bg-red-200 text-red-800 border-red-200'
                    : 'bg-amber/20 hover:bg-amber/20 text-amber border-amber/20'
                  : 'bg-gray-100 text-gray-600',
              )}
            >
              {count}
            </Badge>
          </button>
        ))}
        {activeFilter !== 'ALL' && (
          <button
            onClick={() => onFilterChange('ALL')}
            className='flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors'
          >
            <X className='w-3 h-3' />
            Clear filter
          </button>
        )}
      </div>
    );
  },
);

type RowIssueSortKey =
  | 'level'
  | 'type'
  | 'message'
  | 'rowNumber'
  | 'person'
  | 'fieldName'
  | 'value';

const ROW_ISSUES_PER_PAGE = 30;

const RowIssuesTable = memo(
  ({
    errors,
    onExportErrorReportCsv,
    errorTypeFilter,
    onErrorTypeFilterChange,
  }: {
    errors: ValidationError[];
    onExportErrorReportCsv: () => void;
    errorTypeFilter?: string;
    onErrorTypeFilterChange: (type: string) => void;
  }) => {
    const [sortKey, setSortKey] = useState<RowIssueSortKey | null>(null);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [errorLevelFilter, setErrorLevelFilter] = useState<'ALL' | ValidationError['level']>(
      'ALL',
    );
    const typeFilter = errorTypeFilter ?? 'ALL';

    useEffect(() => {
      setCurrentPage(1);
    }, [typeFilter, errorLevelFilter]);

    const sortedRowErrors = useMemo(() => {
      const filteredErrors = errors
        .filter((error) => {
          if (errorLevelFilter !== 'ALL' && error.level !== errorLevelFilter) return false;
          if (typeFilter !== 'ALL' && error.type !== typeFilter) return false;
          return true;
        })
        .map((error) => {
          let personLabel = '';
          const firstName = error.rowContext?.FIRST_NAME || '';
          const lastName = error.rowContext?.LAST_NAME || '';
          const employeeId = error.rowContext?.EMPLOYEE_ID || '';
          const name = [firstName, lastName].filter(Boolean).join(' ').trim();
          if (name) {
            personLabel = name;
          } else if (employeeId) {
            personLabel = employeeId;
          }
          return {
            ...error,
            person: personLabel.trim(),
          };
        });
      if (!sortKey) return filteredErrors;
      filteredErrors.sort((a, b) => {
        let aVal: string | number;
        let bVal: string | number;
        switch (sortKey) {
          case 'rowNumber':
            aVal = a.rowNumber ?? 0;
            bVal = b.rowNumber ?? 0;
            break;
          case 'level':
            aVal = a.level;
            bVal = b.level;
            break;
          case 'type':
            aVal = a.type;
            bVal = b.type;
            break;
          case 'person':
            aVal = a.person;
            bVal = b.person;
            break;
          case 'fieldName':
            aVal = a.fieldName ?? '';
            bVal = b.fieldName ?? '';
            break;
          case 'value':
            aVal = a.value ?? '';
            bVal = b.value ?? '';
            break;
          case 'message':
            aVal = a.message ?? '';
            bVal = b.message ?? '';
            break;
        }
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          const cmp = aVal.localeCompare(bVal, undefined, {sensitivity: 'base'});
          return sortDirection === 'asc' ? cmp : -cmp;
        }
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
        }
        const cmp = String(aVal).localeCompare(String(bVal));
        return sortDirection === 'asc' ? cmp : -cmp;
      });

      return filteredErrors;
    }, [sortKey, sortDirection, errors, typeFilter, errorLevelFilter]);

    const totalPages = Math.ceil(sortedRowErrors.length / ROW_ISSUES_PER_PAGE);
    const startIndex = (currentPage - 1) * ROW_ISSUES_PER_PAGE;
    const endIndex = startIndex + ROW_ISSUES_PER_PAGE;
    const displayErrors = sortedRowErrors.slice(startIndex, endIndex);

    const onPageChange = useCallback((page: number) => {
      setCurrentPage(page);
    }, []);

    const onSortRowIssues = useCallback(
      (key: RowIssueSortKey) => {
        if (sortKey === key) {
          setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
          setSortKey(key);
          setSortDirection('asc');
        }
        setCurrentPage(1);
      },
      [sortKey],
    );

    const uniqueTypes = useMemo(
      () => Array.from(new Set(errors.map((e) => e.type))).sort((a, b) => a.localeCompare(b)),
      [errors],
    );

    return (
      <>
        <div className='flex items-center justify-between gap-4 min-w-0 flex-wrap'>
          <div className='flex items-center gap-2'>
            <span className='text-sm font-medium text-internal-brand-navy'>Filter:</span>
            <div className='flex gap-1'>
              {(['ALL', 'HIGH', 'LOW'] as const).map((level) => (
                <Button
                  key={level}
                  variant={errorLevelFilter === level ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => {
                    setErrorLevelFilter(level);
                    setCurrentPage(1);
                  }}
                  className='py-5 px-3 text-xs'
                >
                  {level === 'ALL' ? 'All' : level === 'HIGH' ? 'Errors' : 'Warnings'}
                  <Badge
                    variant='secondary'
                    className={`ml-1 text-xs ${errorLevelFilter === level ? 'text-white' : ''}`}
                  >
                    {level === 'ALL'
                      ? errors.length
                      : errors.filter((e) => e.level === level).length}
                  </Badge>
                </Button>
              ))}
            </div>
          </div>
          <Select
            value={typeFilter}
            onValueChange={(value) => {
              onErrorTypeFilterChange(value);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger className='h-11 w-56 border-2 border-neutral-300'>
              <SelectValue placeholder='All issue types' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value='ALL'>All issue types</SelectItem>
              {uniqueTypes.map((type) => (
                <SelectItem key={type} value={type}>
                  {toTitleCase(type.replace(/_/g, ' '))}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div>
            <Button
              variant='outline'
              size='sm'
              onClick={onExportErrorReportCsv}
              className='py-5 px-3 text-xs'
            >
              <Download className='h-4 w-4 mr-2' /> Export row issues as CSV
            </Button>
          </div>
        </div>
        <div className='w-full max-w-full overflow-x-auto'>
          <div className='border border-gray-200 rounded-lg max-w-full'>
            <Table className={'w-full whitespace-nowrap'}>
              <TableHeader className='bg-white'>
                <TableRow className='bg-gray-50'>
                  {(
                    [
                      {value: 'level', label: 'Level'},
                      {value: 'type', label: 'Type'},
                      {value: 'message', label: 'Message'},
                      {value: 'rowNumber', label: 'Row'},
                      {value: 'fieldName', label: 'Field'},
                      {value: 'value', label: 'Value'},
                      {value: 'person', label: 'Person'},
                    ] as {value: RowIssueSortKey; label: string}[]
                  ).map((header) => (
                    <TableHead
                      key={header.value}
                      className='font-semibold cursor-pointer select-none'
                      onClick={() => onSortRowIssues(header.value)}
                    >
                      <div className='flex items-center gap-1'>
                        {header.label}
                        {sortKey === header.value ? (
                          sortDirection === 'asc' ? (
                            <ChevronUp className='h-3 w-3 text-internal-brand-gray' />
                          ) : (
                            <ChevronDown className='h-3 w-3 text-internal-brand-gray' />
                          )
                        ) : (
                          <ArrowUpDown className='h-3 w-3 text-internal-brand-gray opacity-60' />
                        )}
                      </div>
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {displayErrors.map((e, i) => {
                  return (
                    <TableRow key={i} className='hover:bg-gray-50'>
                      <TableCell>
                        {e.level === 'HIGH' ? (
                          <Badge className='bg-red-600 hover:bg-red-600 text-white border-0 font-semibold text-xs'>
                            ERROR
                          </Badge>
                        ) : (
                          <Badge className='bg-amber hover:bg-amber text-white border-0 font-semibold text-xs'>
                            WARN
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className='font-medium'>
                        {toTitleCase(e.type.replace(/_/g, ' '))}
                      </TableCell>
                      <TableCell>
                        <div
                          className={clsx(
                            e.message.length > 40 ? 'min-w-[400px]' : 'min-w-[200px]',
                            'whitespace-normal break-words text-ellipsis line-clamp-3',
                          )}
                          title={e.message}
                        >
                          {e.message}
                        </div>
                      </TableCell>
                      <TableCell className='font-mono text-sm'>
                        <div className='flex items-center gap-1.5'>
                          <span>{e.rowNumber ?? '-'}</span>
                          {e.rowFileId && (
                            <span className='text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-sm'>
                              {e.rowFileId}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className='text-sm max-w-[400px] truncate'>
                        {e.fieldName?.replace(/_/g, ' ') ?? '-'}
                      </TableCell>
                      <TableCell>
                        <div
                          className='max-w-[400px] truncate font-mono text-sm'
                          title={e.value || ''}
                        >
                          {e.value || '-'}
                        </div>
                      </TableCell>
                      <TableCell className='text-sm max-w-[300px] truncate' title={e.person}>
                        {e.person || '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
        {totalPages > 1 && (
          <div className='flex flex-col items-center gap-4'>
            <div className='text-center text-sm text-internal-brand-gray'>
              Showing {startIndex + 1}-{Math.min(endIndex, sortedRowErrors.length)} of{' '}
              {sortedRowErrors.length} errors
            </div>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => currentPage > 1 && onPageChange(currentPage - 1)}
                    className={
                      currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
                {Array.from({length: Math.min(5, totalPages)}, (_, i) => {
                  let pageNumber;
                  if (totalPages <= 5) {
                    pageNumber = i + 1;
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i;
                  } else {
                    pageNumber = currentPage - 2 + i;
                  }

                  return (
                    <PaginationItem key={pageNumber}>
                      <PaginationLink
                        onClick={() => onPageChange(pageNumber)}
                        isActive={currentPage === pageNumber}
                        className='cursor-pointer'
                      >
                        {pageNumber}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                {totalPages > 5 && currentPage < totalPages - 2 && (
                  <>
                    <PaginationItem>
                      <PaginationEllipsis />
                    </PaginationItem>
                    <PaginationItem>
                      <PaginationLink
                        onClick={() => onPageChange(totalPages)}
                        className='cursor-pointer'
                      >
                        {totalPages}
                      </PaginationLink>
                    </PaginationItem>
                  </>
                )}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => currentPage < totalPages && onPageChange(currentPage + 1)}
                    className={
                      currentPage === totalPages
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </>
    );
  },
);

const StructuralErrorsList = memo(
  ({
    structuralErrors,
    onConfigureUnmapped,
  }: {
    structuralErrors: ValidationError[];
    onConfigureUnmapped: () => void;
  }) => {
    const sortedErrors = useMemo(() => {
      return [...structuralErrors].sort((a, b) => {
        if (a.level !== b.level) {
          return a.level === 'HIGH' ? -1 : 1;
        }
        return a.type.localeCompare(b.type);
      });
    }, [structuralErrors]);

    return (
      <div className='space-y-4'>
        {sortedErrors.map((error, index) => (
          <StructuralErrorCard
            key={`${error.type}-${index}`}
            error={error}
            onConfigureUnmapped={onConfigureUnmapped}
          />
        ))}
      </div>
    );
  },
);

type RowIssuesView = 'table' | 'cards';

const RowIssuesViewToggle = memo(
  ({
    view,
    onViewChange,
    className,
  }: {
    view: RowIssuesView;
    onViewChange: (view: RowIssuesView) => void;
    className?: string;
  }) => {
    return (
      <div
        className={clsx(
          'inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-1',
          className,
        )}
      >
        <button
          onClick={() => onViewChange('cards')}
          className={clsx(
            'flex items-center justify-center rounded-md p-2 transition-all',
            view === 'cards'
              ? 'bg-white text-internal-brand-navy shadow-sm'
              : 'text-internal-brand-gray hover:text-internal-brand-navy hover:bg-white/50',
          )}
          title='Card view - grouped by error type'
          aria-label='Card view'
        >
          <LayoutGrid className='h-4 w-4' />
        </button>
        <button
          onClick={() => onViewChange('table')}
          className={clsx(
            'flex items-center justify-center rounded-md p-2 transition-all',
            view === 'table'
              ? 'bg-white text-internal-brand-navy shadow-sm'
              : 'text-internal-brand-gray hover:text-internal-brand-navy hover:bg-white/50',
          )}
          title='Table view - all issues'
          aria-label='Table view'
        >
          <List className='h-4 w-4' />
        </button>
      </div>
    );
  },
);
